/** Client-side image normalize → WebP (JPEG fallback) data URL for catalog uploads. */

export type CompressImageOptions = {
  /** Longest side after resize. */
  maxEdgePx?: number;
  /** Target max size of the encoded binary (before base64). */
  maxBytes?: number;
  initialQuality?: number;
  minQuality?: number;
};

const DEFAULTS = {
  maxEdgePx: 1600,
  maxBytes: 700_000,
  initialQuality: 0.82,
  minQuality: 0.45,
} as const;

function loadImage(source: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(source);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode_failed"));
    };
    img.src = url;
  });
}

function fitSize(width: number, height: number, maxEdge: number): { w: number; h: number } {
  const edge = Math.max(width, height);
  if (edge <= maxEdge) return { w: width, h: height };
  const scale = maxEdge / edge;
  return {
    w: Math.max(1, Math.round(width * scale)),
    h: Math.max(1, Math.round(height * scale)),
  };
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("read_failed"));
    reader.readAsDataURL(blob);
  });
}

async function encodePreferWebp(
  canvas: HTMLCanvasElement,
  quality: number,
): Promise<{ blob: Blob; mime: string } | null> {
  const webp = await canvasToBlob(canvas, "image/webp", quality);
  if (webp && webp.size > 0 && webp.type === "image/webp") {
    return { blob: webp, mime: "image/webp" };
  }
  const jpeg = await canvasToBlob(canvas, "image/jpeg", quality);
  if (jpeg && jpeg.size > 0) {
    return { blob: jpeg, mime: "image/jpeg" };
  }
  return null;
}

/**
 * Resize + compress any browser-decodable image to a compact data URL.
 * Prefer WebP; fall back to JPEG when the browser cannot encode WebP.
 */
export async function compressImageToWebpDataUrl(
  file: File | Blob,
  options: CompressImageOptions = {},
): Promise<string> {
  const maxEdgePx = options.maxEdgePx ?? DEFAULTS.maxEdgePx;
  const maxBytes = options.maxBytes ?? DEFAULTS.maxBytes;
  const minQuality = options.minQuality ?? DEFAULTS.minQuality;
  let quality = options.initialQuality ?? DEFAULTS.initialQuality;

  if (!(file instanceof Blob) || file.size === 0) {
    throw new Error("empty");
  }
  // Guard against pathological multi‑hundred‑MB phone dumps in-memory.
  if (file.size > 40 * 1024 * 1024) {
    throw new Error("source_too_large");
  }

  const img = await loadImage(file);
  let edge = maxEdgePx;
  let best: { blob: Blob; mime: string } | null = null;

  for (let attempt = 0; attempt < 8; attempt++) {
    const { w, h } = fitSize(img.naturalWidth || img.width, img.naturalHeight || img.height, edge);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas_unavailable");
    ctx.drawImage(img, 0, 0, w, h);

    const encoded = await encodePreferWebp(canvas, quality);
    if (!encoded) throw new Error("encode_failed");

    if (!best || encoded.blob.size < best.blob.size) {
      best = encoded;
    }

    if (encoded.blob.size <= maxBytes) {
      return blobToDataUrl(encoded.blob);
    }

    if (quality > minQuality + 0.05) {
      quality = Math.max(minQuality, quality - 0.12);
    } else {
      edge = Math.max(640, Math.round(edge * 0.75));
      quality = Math.max(minQuality, (options.initialQuality ?? DEFAULTS.initialQuality) - 0.1);
    }
  }

  if (!best) throw new Error("encode_failed");
  // Still oversized after retries — return best effort (usually well under old 1.2MB hard reject).
  return blobToDataUrl(best.blob);
}
